import { ParamMap } from '@angular/router';
import { Space } from '../../models/asset.model';
import { resolvedAddressFromSpace } from './address-resolution.utils';

export type SpaceRouteResolve =
  | { kind: 'legacy-id'; documentId: string }
  | { kind: 'slug'; slugCompleto: string };

/** Route segment names that must not be parsed as a space slug or legacy id. */
const RESERVED_SPACE_PATH_SEGMENTS = new Set([
  'calendario',
  'reservar',
  'editar',
  'nuevo',
]);

function isReservedPathSegment(value: string): boolean {
  return RESERVED_SPACE_PATH_SEGMENTS.has(value.trim().toLowerCase());
}

/** Parses `espacio/...` route params (slug segments or legacy Firestore id). */
export function parseSpaceRouteParams(paramMap: ParamMap): SpaceRouteResolve | null {
  const prov = paramMap.get('slugProvincia');
  const dist = paramMap.get('slugDistrito');
  const nombre = paramMap.get('slugNombre');
  if (prov && dist && nombre) {
    if (isReservedPathSegment(nombre) || isReservedPathSegment(prov) || isReservedPathSegment(dist)) {
      return null;
    }
  }

  const legacyId = paramMap.get('id');
  if (legacyId) {
    if (isReservedPathSegment(legacyId)) return null;
    return { kind: 'legacy-id', documentId: legacyId };
  }

  if (!prov || !dist || !nombre) return null;

  const pais = paramMap.get('slugPais');
  const slugCompleto = pais
    ? `${pais.toLowerCase()}/${prov}/${dist}/${nombre}`
    : `${prov}/${dist}/${nombre}`;
  return { kind: 'slug', slugCompleto };
}

/** Router commands for the public space detail URL (`/espacio/...`). */
export function spacePublicPathCommands(
  space: Pick<Space, 'id' | 'slugCompleto' | 'countryCode' | 'province' | 'district' | 'name' | 'address' | 'formattedAddress' | 'postalCode' | 'country' | 'slugProvincia' | 'slugDistrito' | 'googlePlaceId' | 'phone' | 'website' | 'latitude' | 'longitude'>,
): string[] {
  let slug = space.slugCompleto?.trim();
  if (!slug) {
    const resolved = resolvedAddressFromSpace(space, space.name?.trim() || 'espacio');
    slug = resolved?.slugCompleto?.trim();
  }
  if (slug) {
    return ['/espacio', ...slug.split('/').filter(Boolean)];
  }
  return ['/espacio', space.id ?? ''];
}

export function spacePublicPathWithSuffix(
  space: Parameters<typeof spacePublicPathCommands>[0],
  suffix: 'calendario' | 'reservar',
): string[] {
  return [...spacePublicPathCommands(space), suffix];
}

/** True when the space resolves to the public SEO path (stored or computed from location + name). */
export function spaceMatchesPublicSlug(
  space: Pick<Space, 'slugCompleto' | 'countryCode' | 'province' | 'district' | 'name' | 'address' | 'formattedAddress' | 'postalCode' | 'country' | 'slugProvincia' | 'slugDistrito' | 'googlePlaceId' | 'phone' | 'website' | 'latitude' | 'longitude'>,
  slugCompleto: string,
): boolean {
  const target = slugCompleto.trim();
  if (!target) return false;
  const stored = space.slugCompleto?.trim();
  if (stored && stored === target) return true;
  const resolved = resolvedAddressFromSpace(space, space.name?.trim() || 'espacio');
  return resolved?.slugCompleto?.trim() === target;
}
